// Diagnostics for the KI-37 watchdog: it still judges a session on decoded
// frames alone, and nothing here changes what it does about a stall.

// A signal counts as alive if it moved within three stats reports (they arrive
// about once a second), which tolerates a missed report without calling it dead.
const SIGNAL_ALIVE_MS = 3000;
// A 1 s timer that did not run for this long was suspended, not late: a sleeping
// laptop or a discarded tab, where every "nothing arrived" reading is a lie.
export const TICK_SUSPENSION_MS = 5000;

/**
 * Why the watchdog handed a slot back, in this provider's own terms.
 *
 * Billing maps it to a close reason; keeping the vocabulary local is what stops
 * a streaming provider importing from the billing hook.
 */
export enum MytaCutReason {
  Stalled = 'STALLED',
  NeverFramed = 'NEVER_FRAMED',
  /**
   * The seat stopped being ours while the picture was up: the node went away (409), the seat's hold lapsed or
   * was handed to someone else (404), or this page moved off a reclaimed box.
   */
  SeatLost = 'SEAT_LOST',
}

/** Which degradation an `EVENT_SESSION_ISSUE` reports; GameCast's single issue is a capacity one. */
export type MytaIssueKind = 'peer-rebuild' | 'no-capacity';

/**
 * Which layer stopped on the current peer, as far as the browser can prove it.
 *
 * Reported on the analytics event, never a verdict; scoped per peer because a
 * rebuild empties the counters this reads.
 */
export enum MytaFailureClass {
  NeverStarted = 'NEVER_STARTED',
  SenderGone = 'SENDER_GONE',
  EncoderStuck = 'ENCODER_STUCK',
  TransportDead = 'TRANSPORT_DEAD',
  PacketsNoFrames = 'PACKETS_NO_FRAMES',
  DecoderStuck = 'DECODER_STUCK',
  Unknown = 'UNKNOWN',
}

/** The slice of the library's aggregated stats this module reads. */
export interface IMediaStatsReport {
  inboundVideoStats?: {
    framesDecoded?: number;
    framesReceived?: number;
    frameWidth?: number;
    frameHeight?: number;
    framesPerSecond?: number;
    bitrate?: number;
    bytesReceived?: number;
    packetsReceived?: number;
    packetsLost?: number;
    freezeCount?: number;
    codecId?: string;
  };
  /** The library's codec table, keyed by the id `inboundVideoStats.codecId` points at. */
  codecs?: Map<string, { mimeType?: string }>;
  remoteOutboundVideoStats?: {
    reportsSent?: number;
    remoteTimestamp?: number;
    packetsSent?: number;
  };
  candidatePairs?: Array<{
    id?: string;
    selected?: boolean;
    lastPacketReceivedTimestamp?: number;
    currentRoundTripTime?: number;
  }>;
  /** Names the pair in use; Chrome reports no `selected` on the pairs. */
  transportStats?: {
    selectedCandidatePairId?: string;
  };
}

/**
 * Counters the media path reports, each with the local time it last moved.
 *
 * Growth is stamped with our own clock rather than the report's, because RTC
 * stats timestamps live in a different clock domain than `Date.now()`.
 */
export interface INetSnapshot {
  bytes: number;
  bytesAt: number;
  packets: number;
  packetsAt: number;
  framesReceived: number;
  framesReceivedAt: number;
  /** RTCP report count from the sender; a small counter. */
  rtcpReports: number;
  /** The sender's own wall clock on its last report; epoch milliseconds. */
  rtcpRemoteTs: number;
  /** When either RTCP source last advanced: arriving means the UE is alive. */
  rtcpAt: number;
  /** Any packet on the selected ICE pair, video or not — audio included. */
  pairPacket: number;
  pairPacketAt: number;
  packetsLost: number;
  freezeCount: number;
  rtt: number | null;
}

export const EMPTY_NET_SNAPSHOT: INetSnapshot = {
  bytes: 0,
  bytesAt: 0,
  packets: 0,
  packetsAt: 0,
  framesReceived: 0,
  framesReceivedAt: 0,
  rtcpReports: 0,
  rtcpRemoteTs: 0,
  rtcpAt: 0,
  pairPacket: 0,
  pairPacketAt: 0,
  packetsLost: 0,
  freezeCount: 0,
  rtt: null,
};

/**
 * Folds one stats report into the snapshot, keeping when each counter grew.
 *
 * @param previous - Snapshot to advance; a fresh attempt starts from empty.
 * @param aggregated - The report, absent when it carried no stats.
 * @param at - Local time to stamp growth with.
 * @returns The next snapshot; a counter that did not move keeps its old stamp.
 */
export const nextNetSnapshot = (
  previous: INetSnapshot,
  aggregated: IMediaStatsReport | undefined,
  at: number,
): INetSnapshot => {
  const inbound = aggregated?.inboundVideoStats;
  const remote = aggregated?.remoteOutboundVideoStats;
  const pairs = aggregated?.candidatePairs ?? [];
  const transport = aggregated?.transportStats;
  // As `AggregatedStats.getActiveCandidatePair()` resolves it: once the report
  // carries a transport, only its id counts — no pair beats a guess, and an ICE
  // restart leaves several `nominated` pairs with one of them long dead. The
  // `selected` fallback is for reports with no transport at all, not for a
  // transport that has yet to name a pair.
  const pair = transport
    ? pairs.find((p) => p.id === transport.selectedCandidatePairId)
    : pairs.find((p) => p.selected);

  const moved = (next: number, before: number, beforeAt: number) =>
    next > before ? at : beforeAt;

  // A source the report omits is not a counter at zero. Storing 0 keeps the old
  // stamp, but the next report then reads as growth on an unchanged value and
  // stamps a dead signal as alive — which reads as a live transport.
  const carried = (next: number | undefined, before: number) =>
    typeof next === 'number' ? next : before;

  const bytes = carried(inbound?.bytesReceived, previous.bytes);
  const packets = carried(inbound?.packetsReceived, previous.packets);
  const framesReceived = carried(
    inbound?.framesReceived,
    previous.framesReceived,
  );
  // Never folded into one field: a counter and an epoch timestamp are four
  // orders apart, so one report missing the counter would pin the stored value
  // at ~1.7e12 and no later count could ever look like progress again.
  const rtcpReports = carried(remote?.reportsSent, previous.rtcpReports);
  const rtcpRemoteTs = carried(remote?.remoteTimestamp, previous.rtcpRemoteTs);
  const rtcpAdvanced =
    rtcpReports > previous.rtcpReports || rtcpRemoteTs > previous.rtcpRemoteTs;
  const pairPacket = carried(
    pair?.lastPacketReceivedTimestamp,
    previous.pairPacket,
  );

  return {
    bytes,
    bytesAt: moved(bytes, previous.bytes, previous.bytesAt),
    packets,
    packetsAt: moved(packets, previous.packets, previous.packetsAt),
    framesReceived,
    framesReceivedAt: moved(
      framesReceived,
      previous.framesReceived,
      previous.framesReceivedAt,
    ),
    rtcpReports,
    rtcpRemoteTs,
    rtcpAt: rtcpAdvanced ? at : previous.rtcpAt,
    pairPacket,
    pairPacketAt: moved(pairPacket, previous.pairPacket, previous.pairPacketAt),
    packetsLost: carried(inbound?.packetsLost, previous.packetsLost),
    freezeCount: carried(inbound?.freezeCount, previous.freezeCount),
    rtt:
      typeof pair?.currentRoundTripTime === 'number'
        ? pair.currentRoundTripTime
        : previous.rtt,
  };
};

/**
 * Shifts every stamp forward by a gap the timer could not observe.
 *
 * Applied alongside the frame clock so every age in one incident is measured
 * over the same observed window; a stamp of zero never moved at all and stays
 * zero, or the snapshot would read as having once received something.
 *
 * @param snapshot - Snapshot to shift.
 * @param gapMs - Milliseconds the timer did not run for.
 * @returns The shifted snapshot.
 */
export const creditNetGap = (
  snapshot: INetSnapshot,
  gapMs: number,
): INetSnapshot => {
  const shift = (at: number) => (at ? at + gapMs : at);

  return {
    ...snapshot,
    bytesAt: shift(snapshot.bytesAt),
    packetsAt: shift(snapshot.packetsAt),
    framesReceivedAt: shift(snapshot.framesReceivedAt),
    rtcpAt: shift(snapshot.rtcpAt),
    pairPacketAt: shift(snapshot.pairPacketAt),
  };
};

/**
 * Names the layer that stopped, from what the media path last reported.
 *
 * @param props.frames - Frames decoded on the current peer; a rebuild resets it.
 * @param props.streamerPresent - Whether the gateway last listed our streamer;
 * `null` until a list has been received, where its absence proves nothing.
 * @param props.iceState - Last ICE state the library reported.
 * @param props.net - Counters and the times they last moved.
 * @param props.now - Reference time the liveness window is measured against.
 * @returns The class, `Unknown` when no signal is conclusive.
 */
export const classifyMediaFailure = ({
  frames,
  streamerPresent,
  iceState,
  net,
  now,
}: {
  frames: number;
  streamerPresent: boolean | null;
  iceState: string;
  net: INetSnapshot;
  now: number;
}): MytaFailureClass => {
  const alive = (at: number) => at > 0 && now - at < SIGNAL_ALIVE_MS;
  const transportAlive = alive(net.pairPacketAt) || alive(net.rtcpAt);
  const everArrived =
    net.bytesAt > 0 ||
    net.packetsAt > 0 ||
    net.pairPacketAt > 0 ||
    net.rtcpAt > 0;

  // Nothing ever arrived on this attempt, so there is no transport to call dead
  // — it never came up. Tested before the transport, which would claim it.
  if (frames === 0 && !everArrived) {
    return MytaFailureClass.NeverStarted;
  }

  // Split from the silence test below and kept above the list: this is a fresh
  // local observation of our own transport, while a gateway list is remembered
  // information of unknown age.
  if (iceState === 'failed') {
    return MytaFailureClass.TransportDead;
  }

  // Both ride the signalling socket, so they outrank the transport's *silence*,
  // and both test `=== false`: `null` is "no list yet" and proves nothing.
  // `NeverStarted` also needs zero video packets ever — one that sent some and
  // never decoded is a sender that went, not a slot that never came up.
  if (frames === 0 && streamerPresent === false && net.packetsAt === 0) {
    return MytaFailureClass.NeverStarted;
  }

  if (streamerPresent === false) {
    return MytaFailureClass.SenderGone;
  }

  // Ahead of the RTCP inference, which rides the media transport: a dead one
  // silences RTCP too, which would otherwise read as a dead sender.
  if (!transportAlive && !alive(net.bytesAt)) {
    return MytaFailureClass.TransportDead;
  }

  // Only once RTCP has been seen at all, and only while media has stopped: an
  // RTCP gap is no evidence against packets that are arriving right now.
  if (net.rtcpAt > 0 && !alive(net.rtcpAt) && !alive(net.packetsAt)) {
    return MytaFailureClass.SenderGone;
  }

  if (transportAlive && !alive(net.packetsAt)) {
    return MytaFailureClass.EncoderStuck;
  }

  if (alive(net.packetsAt) && !alive(net.framesReceivedAt)) {
    return MytaFailureClass.PacketsNoFrames;
  }

  if (alive(net.framesReceivedAt)) {
    return MytaFailureClass.DecoderStuck;
  }

  return MytaFailureClass.Unknown;
};

/** The seat an incident is attributed to; a lease satisfies it structurally. */
export interface IIncidentSlot {
  leaseId: string;
  nodeId: string;
  slotIndex: number | string;
  streamerId: string;
}

/**
 * What the server said about the seat, latched when it arrived (a refused renew,
 * a node-gone eviction, a spot handoff, the watchdog's own hand-back).
 *
 * `outcome` is the one string a dashboard groups by: `lost:node_gone:<cause>`,
 * `lost:not_current:<actor>`, `released:spot-handoff`, `released:give-up`,
 * `released:never-framed`.
 */
export interface ISeatOutcome {
  outcome: string;
  /** The refusal's own fields; absent where this page released the seat itself. */
  status?: number;
  error?: string;
  cause?: string;
  actor?: string;
  reason?: string;
  at: number;
}

/** A slot event the server reported under this seat (renew `lastEvent`). */
export interface IUeEvent {
  at: string;
  actor: string;
  reason?: string;
}

/** The server's view of the seat from its last renew: which map UE is on, and whether that is a venue. */
export interface IServerView {
  mapName: string | null;
  sessionReady: boolean | null;
  at: number;
}

/** The decoded stream's shape from the last stats report. */
export interface IVideoShape {
  width: number | null;
  height: number | null;
  fps: number | null;
  bitrate: number | null;
  codec: string | null;
}

/**
 * Reads the stream's shape off a stats report, resolving the codec id to its mime type.
 *
 * @param aggregated - The report, absent when it carried no stats.
 * @returns The shape, or `null` when the report has no inbound video at all.
 */
export const videoShapeOf = (
  aggregated: IMediaStatsReport | undefined,
): IVideoShape | null => {
  const inbound = aggregated?.inboundVideoStats;
  if (!inbound) {
    return null;
  }
  const codec = inbound.codecId
    ? aggregated?.codecs?.get(inbound.codecId)?.mimeType ?? inbound.codecId
    : null;
  return {
    width: inbound.frameWidth ?? null,
    height: inbound.frameHeight ?? null,
    fps: inbound.framesPerSecond ?? null,
    bitrate: inbound.bitrate ?? null,
    codec,
  };
};

/**
 * Everything an incident payload is built from, read at the verdict.
 */
export interface IIncidentContext {
  error: string;
  slot: IIncidentSlot;
  /** Decoded frames and when the count last grew. */
  media: { frames: number; at: number };
  /** Whether the lease ever framed; `media.frames` is per peer, not per visit. */
  everFramed: boolean;
  net: INetSnapshot;
  streamerPresent: boolean | null;
  ueRestarting: boolean;
  iceState: string;
  signallingState: string;
  step: string;
  /** Names the degradation on an issue event; absent on a hand-back. */
  issueKind?: MytaIssueKind;
  /** Which seat attempt of this visit the incident belongs to (1-based). */
  leaseAttempt: number;
  /** Peer rebuilds on this lease before the verdict, and how many of them showed a picture. */
  rebuilds: number;
  firstPeerFramed: boolean;
  rebuildsFramed: number;
  seat: ISeatOutcome | null;
  ueEvent: IUeEvent | null;
  server: IServerView | null;
  video: IVideoShape | null;
  now: number;
}

/**
 * Builds the analytics payload for one media incident.
 *
 * Every counter is reported as how long ago it last moved, which is what makes
 * two payloads comparable regardless of when the visit started. The seat, UE
 * event, server view and video fields are the server-side facts §9.2 asked for,
 * read by the Rating step and by the class validation in §9.1.
 *
 * @param context - Signals and identity read at the moment of the verdict.
 * @returns The payload, ready to hand to `trackEventAnalytics`.
 */
export const buildIncidentPayload = ({
  error,
  slot,
  media,
  everFramed,
  net,
  streamerPresent,
  ueRestarting,
  iceState,
  signallingState,
  step,
  issueKind,
  leaseAttempt,
  rebuilds,
  firstPeerFramed,
  rebuildsFramed,
  seat,
  ueEvent,
  server,
  video,
  now,
}: IIncidentContext) => {
  const idleMs = (at: number) => (at ? now - at : null);
  const ueEventAtMs = ueEvent ? Date.parse(ueEvent.at) : NaN;

  return {
    error,
    streamProvider: 'MYTA_STREAMS',
    issueKind: issueKind ?? null,
    leaseAttempt,
    rebuilds,
    firstPeerFramed,
    rebuildsFramed,
    // The seat axis, as the server told it: separate from `failureClass` (the media axis) on purpose.
    seatOutcome: seat?.outcome ?? null,
    seatLostStatus: seat?.status ?? null,
    seatLostCause: seat?.cause ?? null,
    seatLostActor: seat?.actor ?? null,
    seatLostAgoMs: seat ? now - seat.at : null,
    ueEventActor: ueEvent?.actor ?? null,
    ueEventAt: ueEvent?.at ?? null,
    ueEventAgoMs: Number.isFinite(ueEventAtMs) ? now - ueEventAtMs : null,
    serverMap: server?.mapName ?? null,
    serverSessionReady: server?.sessionReady ?? null,
    serverViewAgeMs: server ? now - server.at : null,
    frameWidth: video?.width ?? null,
    frameHeight: video?.height ?? null,
    fps: video?.fps ?? null,
    bitrate: video?.bitrate ?? null,
    codec: video?.codec ?? null,
    // The join key for the lease API's own audit trail, which is where a
    // server-side cause of death would have to be matched up.
    leaseId: slot.leaseId,
    nodeId: slot.nodeId,
    slotIndex: slot.slotIndex,
    streamerId: slot.streamerId,
    // The event type says a session broke; this says which layer went quiet
    // first, because absence of frames names no cause on its own.
    failureClass: classifyMediaFailure({
      frames: media.frames,
      streamerPresent,
      iceState,
      net,
      now,
    }),
    frames: media.frames,
    framesIdleMs: now - media.at,
    everFramed,
    bytes: net.bytes,
    bytesIdleMs: idleMs(net.bytesAt),
    packetsReceived: net.packets,
    packetsIdleMs: idleMs(net.packetsAt),
    framesReceived: net.framesReceived,
    framesReceivedIdleMs: idleMs(net.framesReceivedAt),
    rtcpIdleMs: idleMs(net.rtcpAt),
    pairPacketIdleMs: idleMs(net.pairPacketAt),
    packetsLost: net.packetsLost,
    freezeCount: net.freezeCount,
    rtt: net.rtt,
    // A streamer we saw leave the gateway's list is the one signal that proves
    // the slot died; still-present can also mean we lost contact, and `null`
    // that no list ever reached us.
    streamerPresent,
    ueRestarting,
    iceState,
    signallingState,
    step,
    onLine: typeof navigator === 'undefined' ? null : navigator.onLine,
  };
};

/** The ladder's answer while a seat is awaited; the lease API's 409 body. */
export interface ICapacityWait {
  phase?: string;
  etaSec?: number;
  retryAfterSec?: number;
  provisioning?: boolean;
  idleFree?: number;
  hint?: string;
}

/**
 * Builds the issue payload for a visit that has waited for capacity too long.
 *
 * The comparable event on GameCast is "no available streaming providers"; here
 * the ladder's own phase says what the pool is doing about it.
 *
 * @param props.details - The last 409 body, absent when none carried one.
 * @param props.leaseAttempt - Which seat attempt is waiting (1-based).
 * @param props.waitedMs - Time since the first refusal of this attempt.
 * @param props.eventId - The experience being entered.
 * @returns The payload, ready to hand to `trackEventAnalytics`.
 */
export const buildCapacityIssuePayload = ({
  details,
  leaseAttempt,
  waitedMs,
  eventId,
}: {
  details: ICapacityWait | null;
  leaseAttempt: number;
  waitedMs: number;
  eventId: string | undefined;
}) => ({
  error: `no free slot for ${Math.round(waitedMs / 1000)}s`,
  streamProvider: 'MYTA_STREAMS',
  issueKind: 'no-capacity' as MytaIssueKind,
  phase: details?.phase ?? null,
  etaSec: details?.etaSec ?? null,
  retryAfterSec: details?.retryAfterSec ?? null,
  provisioning: details?.provisioning ?? null,
  idleFree: details?.idleFree ?? null,
  leaseAttempt,
  waitedMs,
  eventId: eventId ?? null,
});
